import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import es from './locales/es.json';

/**
 * Translation runtime (2026-07-29).
 *
 * Isaac's rule: a Spanish user's whole world is Spanish, an English user's is
 * English, and English is the default. Language is a property of the PERSON —
 * it lives on `users.preferred_language` — not of the device or the browser, so
 * a tech gets their own language on any shop tablet they sign into.
 *
 * That is also why there is no language detector here. Detecting from
 * `navigator.language` would hand a shared tablet whatever the last person's OS
 * was set to, and would silently disagree with the language Roldi and Whisper
 * are already using — both of which read `preferred_language` server-side. One
 * field drives all three; guessing would fork them.
 */

export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  // Endonym, deliberately: someone looking for Spanish scans for "Español",
  // not for the English word for it.
  es: 'Español',
};

/** Anything unrecognised resolves to English, the designed fallback. */
export function normalizeLanguage(value: string | null | undefined): SupportedLanguage {
  return value === 'es' ? 'es' : 'en';
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, es: { translation: es } },
  lng: 'en',
  fallbackLng: 'en',
  // A key with no Spanish yet renders its English rather than the raw key.
  // During the phased sweep that is the difference between an untranslated
  // screen and a screen full of `tech.home.greeting`.
  returnEmptyString: false,
  interpolation: {
    // React already escapes. Escaping again double-encodes apostrophes, which
    // Spanish copy is full of.
    escapeValue: false,
  },
});

/**
 * Point the document at the active language. Not cosmetic: screen readers pick
 * pronunciation from `lang`, so a Spanish page left marked `en` is read aloud
 * with English phonemes.
 */
export function applyDocumentLanguage(language: SupportedLanguage): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}

export default i18n;
